/* STEEDIU PLUS — Gateway 3D "+"
   A machined, studio-lit extruded plus: physical clearcoat material + a procedural
   studio environment for real reflections + a soft contact shadow + organic motion.
   One instance per .sd-gw section. Fails silent if WebGL/THREE is unavailable. */
(function () {
  'use strict';

  function hexToInt(hex) {
    if (!hex) return 0x0000fe;
    var m = String(hex).trim().replace('#', '');
    if (m.length === 3) m = m.split('').map(function (c) { return c + c; }).join('');
    var n = parseInt(m, 16);
    return isNaN(n) ? 0x0000fe : n;
  }

  // Studio "lighting rig" painted to a canvas → PMREM → crisp moving reflections.
  function makeStudioEnv(renderer) {
    var c = document.createElement('canvas'); c.width = 256; c.height = 256;
    var x = c.getContext('2d');
    x.fillStyle = '#0a0b12'; x.fillRect(0, 0, 256, 256);            // dark room
    x.filter = 'blur(10px)';
    // big key soft-box, upper area
    x.fillStyle = '#ffffff'; x.fillRect(28, 12, 150, 70);
    // secondary fill box, right — cool white (no lavender)
    x.fillStyle = '#eef3ff'; x.fillRect(180, 60, 60, 90);
    // rim strip, lower left — clean blue
    x.fillStyle = '#3f6dff'; x.fillRect(10, 150, 90, 26);
    // faint gradient floor glow — blue, not grey-purple
    var g = x.createLinearGradient(0, 150, 0, 256);
    g.addColorStop(0, 'rgba(40,60,120,0)'); g.addColorStop(1, 'rgba(70,110,220,0.5)');
    x.fillStyle = g; x.fillRect(0, 150, 256, 106);
    x.filter = 'none';
    var tex = new THREE.CanvasTexture(c);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    var pmrem = new THREE.PMREMGenerator(renderer);
    var env = pmrem.fromEquirectangular(tex).texture;
    tex.dispose(); pmrem.dispose();
    return env;
  }

  function buildPlusGeometry() {
    var t = 0.60, L = 1.75;
    var s = new THREE.Shape();
    s.moveTo(-t, -L);
    s.lineTo(t, -L); s.lineTo(t, -t);
    s.lineTo(L, -t); s.lineTo(L, t);
    s.lineTo(t, t); s.lineTo(t, L);
    s.lineTo(-t, L); s.lineTo(-t, t);
    s.lineTo(-L, t); s.lineTo(-L, -t);
    s.lineTo(-t, -t); s.closePath();
    var geo = new THREE.ExtrudeGeometry(s, {
      depth: 0.72, bevelEnabled: true, bevelThickness: 0.13,
      bevelSize: 0.14, bevelSegments: 18, steps: 1, curveSegments: 16
    });
    geo.center();
    return geo;
  }

  function initGateway(section) {
    if (!window.THREE) return;
    var mount = section.querySelector('[data-plus]');
    if (!mount) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var motionOn = section.dataset.motion === 'on' && !reduced;
    var speed = parseFloat(section.dataset.speed) || 0.6;
    var color = hexToInt(section.dataset.plusColor);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    camera.position.set(0, 0, 7.6);

    var renderer;
    try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); }
    catch (e) { return; }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    if ('outputEncoding' in renderer) renderer.outputEncoding = THREE.sRGBEncoding;
    // Linear tone mapping preserves hue — ACES pushes pure blue toward purple/magenta.
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.toneMappingExposure = 1.0;
    mount.appendChild(renderer.domElement);

    var env = null;
    try { env = makeStudioEnv(renderer); scene.environment = env; } catch (e) { env = null; }

    // Physical clearcoat material — glossy machined finish
    var material = new THREE.MeshPhysicalMaterial({
      color: color, metalness: 0.55, roughness: 0.16,
      clearcoat: 1.0, clearcoatRoughness: 0.14,
      reflectivity: 0.6, envMap: env, envMapIntensity: 1.15
    });
    var mesh = new THREE.Mesh(buildPlusGeometry(), material);
    scene.add(mesh);

    // Upgrade to real HDR image-based lighting (same studio env as the About tools).
    // Neutral studio keeps the blue pure; silently keeps the procedural fallback on error.
    var hdrUrl = section.dataset.hdr;
    if (hdrUrl && THREE.RGBELoader) {
      try {
        new THREE.RGBELoader().load(hdrUrl, function (tex) {
          try {
            tex.mapping = THREE.EquirectangularReflectionMapping;
            var pm = new THREE.PMREMGenerator(renderer);
            var hdrEnv = pm.fromEquirectangular(tex).texture;
            tex.dispose(); pm.dispose();
            scene.environment = hdrEnv;
            material.envMap = hdrEnv; material.needsUpdate = true;
            if (env && env.dispose) env.dispose();
          } catch (e) {}
        }, undefined, function () { /* keep procedural fallback */ });
      } catch (e) {}
    }

    // Lighting (env does most of it; these add crisp speculars)
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    var key = new THREE.DirectionalLight(0xffffff, 1.0); key.position.set(5, 6, 8); scene.add(key);
    var fill = new THREE.DirectionalLight(0xdfe4ff, 0.35); fill.position.set(-7, -2, 4); scene.add(fill);
    var rim = new THREE.DirectionalLight(0xffffff, 0.6); rim.position.set(0, 3, -6); scene.add(rim);

    function size() {
      var w = mount.clientWidth || 1, h = mount.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h; camera.updateProjectionMatrix();
    }
    size();

    // Interaction: drag to rotate (touch + mouse) with momentum; ambient auto-spin when idle
    var rotY = -0.5, rotX = 0, velY = 0, velX = 0;
    var dragging = false, moved = false, lastX = 0, lastY = 0, downX = 0, downY = 0;

    if (motionOn) {
      section.style.touchAction = 'none';
      section.addEventListener('pointerdown', function (e) {
        dragging = true; moved = false;
        lastX = downX = e.clientX; lastY = downY = e.clientY;
      });
      window.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - lastX, dy = e.clientY - lastY;
        lastX = e.clientX; lastY = e.clientY;
        if (Math.abs(e.clientX - downX) + Math.abs(e.clientY - downY) > 6) moved = true;
        velY = dx * 0.008; velX = dy * 0.006;
        rotY += velY; rotX += velX;
      });
      window.addEventListener('pointerup', function () { dragging = false; });
      window.addEventListener('pointercancel', function () { dragging = false; });
      // if the pointer was dragged, don't follow the half's link on release
      var halves = section.querySelectorAll('.sd-gw__half');
      for (var hi = 0; hi < halves.length; hi++) {
        halves[hi].addEventListener('click', function (e) { if (moved) { e.preventDefault(); moved = false; } });
      }
    }

    var t0 = performance.now(), running = false, raf = 0;

    function frame(now) {
      if (!running) return;
      var dt = Math.min((now - t0) / 1000, 0.05); t0 = now;
      if (!dragging) {
        rotY += speed * 0.6 * dt;         // ambient auto-spin
        rotY += velY; rotX += velX;        // momentum carry after release
        velY *= 0.94; velX *= 0.90;
        rotX += (0 - rotX) * 0.015;        // gently settle X back to level (head-on)
      }
      mesh.rotation.y = rotY;
      mesh.rotation.x = rotX + Math.sin(rotY * 0.5) * 0.06;
      mesh.position.y = Math.sin(rotY * 0.8) * 0.05;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(frame);
    }
    function start() { if (running) return; running = true; t0 = performance.now(); raf = requestAnimationFrame(frame); }
    function stop() { running = false; if (raf) cancelAnimationFrame(raf); }

    if (motionOn) {
      start();
      document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });
    } else {
      mesh.rotation.set(0, -0.5, 0);
      renderer.render(scene, camera);
    }

    if (window.ResizeObserver) {
      new ResizeObserver(function () { size(); if (!motionOn) renderer.render(scene, camera); }).observe(mount);
    } else {
      window.addEventListener('resize', function () { size(); if (!motionOn) renderer.render(scene, camera); });
    }
  }

  function boot() {
    var sections = document.querySelectorAll('.sd-gw');
    for (var i = 0; i < sections.length; i++) initGateway(sections[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
